/**
 * useJourneyView — DRR-309 Phase 2-4
 *
 * Data aggregation hook for JourneyView component.
 * Combines instance data, workflow metadata, and task data into
 * a unified view optimized for goal-centric UI.
 *
 * Phase 4: Real-time subscriptions for live updates.
 */

import { useMemo, useEffect, useCallback } from 'react';
import { useQuery, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
// Status constants for comparison (type sharing between packages pending)
const GqlInstanceStatus = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  TIMED_OUT: 'TIMED_OUT',
  BLOCKED: 'BLOCKED',
  SCHEDULED: 'SCHEDULED',
} as const;

const GqlTaskStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  BLOCKED: 'BLOCKED',
  DELEGATED: 'DELEGATED',
  WAITING_APPROVAL: 'WAITING_APPROVAL',
  SKIPPED: 'SKIPPED',
} as const;
import type { JourneyViewData, JourneyTrack, AvailableTransition } from '../types';
import { useNextAction } from './useNextAction';

// Query response types for type safety
interface GetJourneyViewData {
  getInstance: {
    id: string;
    workflowDefinitionId: string;
    instanceName: string;
    workflowName: string;
    workflowDescription?: string;
    subjectName?: string;
    subjectActorId?: string;
    currentState: string;
    status: string;
    progressPercent: number;
    totalTasks: number;
    completedTasks: number;
    startedAt?: string;
    completedAt?: string;
    errorMessage?: string;
    availableTransitions: Array<{
      event: string;
      toState: string;
      displayName?: string;
      guard?: string;
      guardPassed: boolean;
    }>;
    tracks?: Array<{
      trackId: string;
      instanceId: string;
      fragmentId: string;
      displayName: string;
      phase: string;
      status: string;
      currentState: string;
      isPaused: boolean;
      pausedAtState?: string;
      startedAt?: string;
      progressPercent: number;
    }>;
  } | null;
  taskApiGetTasksByWorkflowInstance: Array<{
    id: string;
    name: string;
    status: string;
    priority?: number;
    dueAt?: string;
    mandateRef?: string;
    taskGroupRef?: string;
    createdAt?: string;
  }>;
}

interface InstanceStateChangedData {
  instanceStateChanged: {
    instanceId: string;
    previousState: string;
    currentState: string;
    status: string;
    timestamp: string;
    triggeredBy?: string;
    context?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  } | null;
}

// GraphQL query for journey view data
const GET_JOURNEY_VIEW = gql`
  query GetJourneyView($tenantId: String!, $instanceId: String!) {
    getInstance(tenantId: $tenantId, instanceId: $instanceId) {
      id
      workflowDefinitionId
      instanceName
      workflowName
      workflowDescription
      subjectName
      subjectActorId
      currentState
      status
      progressPercent
      totalTasks
      completedTasks
      startedAt
      completedAt
      errorMessage
      availableTransitions {
        event
        toState
        displayName
        guard
        guardPassed
      }
      # TODO: Uncomment when tracks field is added to backend GqlInstance (DRR-309 Phase 1)
      # tracks {
      #   trackId
      #   instanceId
      #   fragmentId
      #   displayName
      #   phase
      #   status
      #   currentState
      #   isPaused
      #   pausedAtState
      #   startedAt
      #   progressPercent
      # }
    }

    taskApiGetTasksByWorkflowInstance(workflowInstanceId: $instanceId) {
      id
      name
      status
      priority
      dueAt
      mandateRef
      taskGroupRef
      createdAt
    }
  }
`;

// DRR-309 Phase 4: Real-time subscription for instance state changes
const INSTANCE_STATE_CHANGED = gql`
  subscription InstanceStateChanged($tenant: String!, $instanceId: String) {
    instanceStateChanged(tenant: $tenant, instanceId: $instanceId) {
      instanceId
      previousState
      currentState
      status
      timestamp
      triggeredBy
      context
      metadata
    }
  }
`;

interface UseJourneyViewOptions {
  /** Poll interval in ms (0 to disable). Set to 0 when using subscriptions. */
  pollInterval?: number;
  /** Skip the query */
  skip?: boolean;
  /** Enable real-time subscriptions (default: true) */
  enableSubscription?: boolean;
}

export function useJourneyView(
  tenantId: string,
  instanceId: string,
  options: UseJourneyViewOptions = {}
): JourneyViewData {
  const { pollInterval = 0, skip = false, enableSubscription = true } = options;

  // Use poll interval only when subscription is disabled
  const effectivePollInterval = enableSubscription ? 0 : pollInterval;

  const { data, loading, refetch } = useQuery<GetJourneyViewData>(GET_JOURNEY_VIEW, {
    variables: { tenantId, instanceId },
    skip: skip || !tenantId || !instanceId,
    pollInterval: effectivePollInterval,
    fetchPolicy: 'cache-and-network',
  });

  // DRR-309 Phase 4: Subscribe to real-time state changes
  const { data: subscriptionData } = useSubscription<InstanceStateChangedData>(INSTANCE_STATE_CHANGED, {
    variables: { tenant: tenantId, instanceId },
    skip: skip || !tenantId || !instanceId || !enableSubscription,
    onData: ({ data: subData }) => {
      // When we receive a state change, refetch the full journey data
      // to get updated tasks, transitions, and progress
      if (subData?.data?.instanceStateChanged) {
        refetch();
      }
    },
  });

  // Refetch when subscription receives state change (backup if onData doesn't trigger)
  const handleSubscriptionUpdate = useCallback(() => {
    if (subscriptionData?.instanceStateChanged) {
      refetch();
    }
  }, [subscriptionData, refetch]);

  useEffect(() => {
    handleSubscriptionUpdate();
  }, [handleSubscriptionUpdate]);

  const instance = data?.getInstance;
  const tasks = data?.taskApiGetTasksByWorkflowInstance ?? [];

  // Calculate day number from startedAt
  const dayNumber = useMemo(() => {
    if (!instance?.startedAt) return 1;
    const start = new Date(instance.startedAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  }, [instance?.startedAt]);

  // Map tracks from GraphQL response
  const tracks: JourneyTrack[] = useMemo(() => {
    return (instance?.tracks ?? []).map((t: any) => ({
      trackId: t.trackId,
      instanceId: t.instanceId,
      fragmentId: t.fragmentId,
      displayName: t.displayName,
      phase: t.phase,
      status: t.status,
      currentState: t.currentState,
      isPaused: t.isPaused,
      pausedAtState: t.pausedAtState,
      startedAt: t.startedAt,
      progressPercent: t.progressPercent,
    }));
  }, [instance?.tracks]);

  // Map available transitions
  const availableTransitions: AvailableTransition[] = useMemo(() => {
    return (instance?.availableTransitions ?? []).map((t: any) => ({
      event: t.event,
      toState: t.toState,
      displayName: t.displayName,
      guard: t.guard,
      guardPassed: t.guardPassed,
    }));
  }, [instance?.availableTransitions]);

  // Map tasks to frontend-compatible format (backend: name/dueAt → frontend: title/deadline)
  const mappedTasks = useMemo(() => {
    return tasks.map((t) => ({
      id: t.id,
      title: t.name,
      status: t.status,
      priority: t.priority ? String(t.priority) : null,
      deadline: t.dueAt ?? null,
      mandateRef: t.mandateRef ?? null,
      taskGroupRef: t.taskGroupRef ?? null,
    }));
  }, [tasks]);

  // Get next action using the dedicated hook
  const nextAction = useNextAction(mappedTasks, availableTransitions);

  // Filter tasks by urgency (DRR-309: don't overwhelm with all tasks)
  const { tasksForToday, tasksDueSoon, pendingTaskCount } = useMemo(() => {
    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    const in48Hours = new Date(now.getTime() + 48 * 60 * 60 * 1000);

    const actionable = mappedTasks.filter(
      (t) => t.status === GqlTaskStatus.PENDING || t.status === GqlTaskStatus.IN_PROGRESS
    );

    const forToday = actionable
      .filter((t) => {
        if (!t.deadline) return false;
        const deadline = new Date(t.deadline);
        return deadline <= endOfToday;
      })
      .map((t) => ({ id: t.id, title: t.title, status: t.status, deadline: t.deadline }));

    const dueSoon = actionable
      .filter((t) => {
        if (!t.deadline) return false;
        const deadline = new Date(t.deadline);
        return deadline > endOfToday && deadline <= in48Hours;
      })
      .map((t) => ({ id: t.id, title: t.title, status: t.status, deadline: t.deadline }));

    return {
      tasksForToday: forToday,
      tasksDueSoon: dueSoon,
      pendingTaskCount: actionable.length,
    };
  }, [mappedTasks]);

  // Filter tasks with regulatory mandates for deadline overlay
  const regulatoryTasks = useMemo(() => {
    return tasks
      .filter((t: any) => t.mandateRef && t.dueAt)
      .map((t: any) => ({
        id: t.id,
        title: t.name,
        status: t.status,
        mandateRef: t.mandateRef,
        deadline: t.dueAt,
        createdAt: t.createdAt,
      }));
  }, [tasks]);

  // Determine completion and blocked states
  const isComplete = instance?.status === GqlInstanceStatus.COMPLETED;
  const isBlocked =
    instance?.status === GqlInstanceStatus.PAUSED ||
    instance?.status === GqlInstanceStatus.FAILED ||
    (availableTransitions.length > 0 &&
      availableTransitions.every((t) => !t.guardPassed));

  return {
    instanceId,
    workflowId: instance?.workflowDefinitionId ?? '',
    goalName: instance?.workflowName ?? instance?.instanceName ?? 'Journey',
    goalDescription: instance?.workflowDescription,
    subjectName: instance?.subjectName ?? 'Subject',
    subjectActorId: instance?.subjectActorId,
    currentState: instance?.currentState ?? '',
    status: instance?.status ?? 'PENDING',
    progressPercent: instance?.progressPercent ?? 0,
    totalTasks: instance?.totalTasks ?? 0,
    completedTasks: instance?.completedTasks ?? 0,
    dayNumber,
    startedAt: instance?.startedAt ? new Date(instance.startedAt) : new Date(),
    completedAt: instance?.completedAt ? new Date(instance.completedAt) : null,
    tracks,
    availableTransitions,
    nextAction,
    isComplete,
    isBlocked,
    errorMessage: instance?.errorMessage,
    loading,
    // Filtered task views (DRR-309: don't overwhelm)
    tasksForToday,
    tasksDueSoon,
    pendingTaskCount,
    // Tasks with regulatory mandates for deadline overlay
    regulatoryTasks,
  };
}

export default useJourneyView;
